import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Input from '../../components/input/Input';
import Table from '../../components/table/table';
import Icon from '../../components/icon/Icon';
import type { MonetizationRequestSearchResponse, MonetizationRequestProduct } from './types';
import { useFiscalIdConfig } from '../../hooks/useFiscalIdConfig';
import { useConfig } from '../../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../../utils/quotationAssets';
import {
  currencySymbol,
  isoCodeForGuaranteeCurrency,
} from '../../utils/currencyLabel';

interface ProductSelectionStepProps {
  searchResult: MonetizationRequestSearchResponse;
  onProductSelect: (product: MonetizationRequestProduct) => void;
  onNewSearch: () => void;
  cuit: string;
}

const formatCurrency = (value: number | null | undefined, currency: string = '$'): string => {
  if (value === null || value === undefined) return '-';
  return `${currency} ${value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

interface ProductRow extends MonetizationRequestProduct {
  originalProduct: MonetizationRequestProduct;
}

const ProductSelectionStep = ({ 
  searchResult, 
  onProductSelect,
  onNewSearch,
  cuit: searchedCuit,
}: ProductSelectionStepProps) => {
  const fiscalId = useFiscalIdConfig();
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const localSymbol = currencySymbol(localCurrencyCode);
  const { cards, products, companyName, cuit } = searchResult;

  const isOperable = (row: ProductRow) => row.availableOperable > 0;
  const symbolFor = (row: ProductRow) =>
    currencySymbol(
      isoCodeForGuaranteeCurrency(
        {
          currencyId: row.currencyId,
          currencyLabel: row.currencyLabel,
        },
        localCurrencyCode
      )
    );

  const handleProductSelect = (product: MonetizationRequestProduct) => {
    if (product.availableOperable <= 0) return;
    onProductSelect(product);
  };

  const columns = [
    { 
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Producto</P>,
      selector: (row: ProductRow) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false} modifier={isOperable(row) ? 'regular' : 'weak'}>{row.productLabel}</P>
      ),
      grow: 1.5,
    },
    { 
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Divisa</P>,
      selector: (row: ProductRow) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false} modifier={isOperable(row) ? 'regular' : 'weak'}>{isoCodeForGuaranteeCurrency({ currencyId: row.currencyId, currencyLabel: row.currencyLabel }, localCurrencyCode)}</P>
      ),
      grow: 0.8,
    },
    { 
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Cap producto</P>,
      selector: (row: ProductRow) => {
        const symbol = symbolFor(row);
        return <P variant='secondary-2' type="3" weight="regular" withMargin={false} modifier={isOperable(row) ? 'regular' : 'weak'}>{formatCurrency(row.capProduct, symbol)}</P>;
      },
      grow: 1,
    },
    { 
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Utilizado</P>,
      selector: (row: ProductRow) => {
        const symbol = symbolFor(row);
        return <P variant='secondary-2' type="3" weight="regular" withMargin={false} modifier={isOperable(row) ? 'regular' : 'weak'}>{formatCurrency(row.usedProduct, symbol)}</P>;
      },
      grow: 1,
    },
    { 
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Disp. producto</P>,
      selector: (row: ProductRow) => {
        const symbol = symbolFor(row);
        return <P variant='secondary-2' type="3" weight="regular" withMargin={false} modifier={isOperable(row) ? 'regular' : 'weak'}>{formatCurrency(row.availableProduct, symbol)}</P>;
      },
      grow: 1,
    },
    { 
name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>{fiscalId.availableCurrencyLabel}</P>,
      selector: (row: ProductRow) => {
        const symbol = symbolFor(row);
        return <P variant='secondary-2' type="3" weight="regular" withMargin={false} modifier={isOperable(row) ? 'regular' : 'weak'}>{formatCurrency(row.availableCuitCurrency, symbol)}</P>;
      },
      grow: 1,
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Acción</P>,
      selector: (row: ProductRow) => (
        <Card 
          display='flex' 
          justifyContent='center'
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleProductSelect(row.originalProduct);
          }}
          style={{ cursor: isOperable(row) ? 'pointer' : 'not-allowed', opacity: isOperable(row) ? 1 : 0.4 }}
        >
          <Icon name='arrow_right_outline' variant={isOperable(row) ? 'primary' : 'neutral'} />
        </Card>
      ),
      grow: 0.5,
    }
  ];

  const tableData: ProductRow[] = products.map((product) => ({
    ...product,
    originalProduct: product,
  }));

  const handleRowClick = (row: ProductRow) => {
    handleProductSelect(row.originalProduct);
  };

  return (
    <Card width='100%'>
      <Card margin={{ bottom: '4' }}>
        <P variant='neutral' modifier='strong' weight='bold'>
          Ingresá el {fiscalId.label} del cliente para ver sus productos habilitados
        </P>
      </Card>

      <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '4' }}>
        <Card width='300px'>
          <Input
            type='text'
            size='md'
            icons={{ iconLeft: 'search_outline' }}
            placeholder={fiscalId.enterPlaceholder}
            value={searchedCuit}
            disabled
          />
        </Card>
        <P
          variant='primary'
          weight='bold'
          withMargin={false}
          style={{ cursor: 'pointer', textDecoration: 'none' }}
          onClick={onNewSearch}
        >
          Nueva búsqueda
        </P>
      </Card>

      <Card margin={{ bottom: '4' }}>
        <H type='4' weight='bold' modifier='strong' withMargin={false} style={{ marginBottom: 8 }}>
          {companyName || 'Cliente'}
        </H>
        <P variant='neutral' modifier='weak' withMargin={false}>
          {fiscalId.label}: {fiscalId.format(cuit)}
        </P>
      </Card>

      {cards && (
        <Card display='flex' gap='16px' margin={{ bottom: '4' }} flexWrap='wrap'>
          <Card
            spacing='4'
            borderRadiusAmount='2'
            style={{ flex: 1, border: '1px solid #DFE6EB', backgroundColor: '#F5F7FA', minWidth: '280px', padding: '20px 24px' }}
          >
            <P variant='neutral' modifier='strong' weight='bold' withMargin={false} style={{ marginBottom: 16 }}>
              Cupo {localCurrencyCode}
            </P>
            <div
              data-testid='client-line-metrics-row'
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                width: '100%',
                gap: '16px',
              }}
            >
              <Card>
                <H type='3' weight='bold' modifier='strong' withMargin={false}>
                  {formatCurrency(cards.capArs, localSymbol)}
                </H>
                <P variant='neutral' modifier='weak' type='3' withMargin={false}>Otorgado</P>
              </Card>
              <Card>
                <H type='3' weight='bold' modifier='strong' withMargin={false}>
                  {formatCurrency(cards.usedArs, localSymbol)}
                </H>
                <P variant='neutral' modifier='weak' type='3' withMargin={false}>Utilizado</P>
              </Card>
              <Card>
                <H type='3' weight='bold' modifier='strong' withMargin={false}>
                  {formatCurrency(cards.availableArs, localSymbol)}
                </H>
                <P variant='neutral' modifier='weak' type='3' withMargin={false}>Disponible</P>
              </Card>
            </div>
          </Card>

          <Card
            spacing='4'
            borderRadiusAmount='2'
            style={{ flex: 1, border: '1px solid #DFE6EB', backgroundColor: '#F5F7FA', minWidth: '280px', padding: '20px 24px' }}
          >
            <P variant='neutral' modifier='strong' weight='bold' withMargin={false} style={{ marginBottom: 16 }}>
              Linea Cliente USD
            </P>
            <div
              data-testid='client-line-metrics-row'
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                width: '100%',
                gap: '16px',
              }}
            >
              <Card>
                <H type='3' weight='bold' modifier='strong' withMargin={false}>
                  {formatCurrency(cards.capUsd, 'U$D')}
                </H>
                <P variant='neutral' modifier='weak' type='3' withMargin={false}>Otorgado</P>
              </Card>
              <Card>
                <H type='3' weight='bold' modifier='strong' withMargin={false}>
                  {formatCurrency(cards.usedUsd, 'U$D')}
                </H>
                <P variant='neutral' modifier='weak' type='3' withMargin={false}>Utilizado</P>
              </Card>
              <Card>
                <H type='3' weight='bold' modifier='strong' withMargin={false}>
                  {formatCurrency(cards.availableUsd, 'U$D')}
                </H>
                <P variant='neutral' modifier='weak' type='3' withMargin={false}>Disponible</P>
              </Card>
            </div>
          </Card>
        </Card>
      )}

      <Card margin={{ top: '2' }}>
        <H type='5' weight='bold' modifier='strong' withMargin={false} style={{ marginBottom: 16 }}>
          Productos habilitados
        </H>
        <P variant='neutral' modifier='weak' type='2' style={{ marginBottom: 16 }}>
          Seleccioná un producto operable haciendo clic en la fila o en el ícono de flecha para continuar.
        </P>
        
        <Table
          columns={columns}
          data={tableData}
          tableData={{ size: tableData.length, totalElements: tableData.length }}
          onRowClicked={handleRowClick}
          variant='normal'
          disablePagination
          conditionalRowStyles={[
            {
              when: (row: ProductRow) => row.availableOperable <= 0,
              style: { opacity: 0.55, cursor: 'not-allowed' },
            },
          ]}
        />
      </Card>
    </Card>
  );
};

export default ProductSelectionStep;
